import { api } from '@/api';
import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import {
  readSlotOccupancy,
  type SlotOccupancyPeriod,
  type SlotOccupancyTarget,
} from '@/api/core/inventory';
import type { ServiceClient } from '@/api/http/client';
import { isApiError } from '@/api/http/errors';
import { operatorConfig, operatorSession } from '@/auth/runtime';
import type { OperatorSession } from '@/auth/session';

export interface SlotOccupancyRuntime {
  client: ServiceClient;
  session: OperatorSession;
  apiNamespace: string;
}
export const slotOccupancyRuntime: SlotOccupancyRuntime = {
  client: api.core,
  session: operatorSession,
  apiNamespace: operatorConfig?.coreApiUrl ?? 'unconfigured',
};

export function hasInventoryReadAccess(session: OperatorSession, generation: number): boolean {
  const state = session.getSnapshot();
  return (
    state.generation === generation &&
    state.status === 'authorized' &&
    !!state.authorization &&
    !state.deniedDomains.includes('inventory') &&
    canReadDomain(state.authorization, 'inventory', 16)
  );
}

export function slotOccupancyQuery(
  runtime: SlotOccupancyRuntime,
  generation: number,
  propertyId: string,
  roomId: string,
  slotId: string,
  period: SlotOccupancyPeriod,
  enabled: boolean,
  isCurrent: () => boolean,
) {
  return {
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'inventory-slot-occupancy', propertyId),
      roomId,
      slotId,
      period.startsAt,
      period.endsAt,
    ],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!hasInventoryReadAccess(runtime.session, generation) || !isCurrent())
        throw new Error('현재 객실 점유 조회 권한 또는 선택 대상을 확인할 수 없습니다.');
      try {
        const result = await readSlotOccupancy(
          runtime.client,
          slotId,
          period,
          { propertyId, roomId } satisfies SlotOccupancyTarget,
          signal,
        );
        if (signal.aborted || !isCurrent() || !hasInventoryReadAccess(runtime.session, generation))
          throw new Error('현재 객실 점유 조회 권한 또는 선택 대상을 확인할 수 없습니다.');
        return result;
      } catch (error) {
        const current =
          !signal.aborted &&
          isCurrent() &&
          runtime.session.getSnapshot().generation === generation &&
          runtime.session.getSnapshot().status === 'authorized';
        if (current && isApiError(error) && error.status === 403)
          runtime.session.reportDomainAccessDenied('inventory', generation);
        throw error;
      }
    },
    enabled: enabled && hasInventoryReadAccess(runtime.session, generation) && isCurrent(),
    staleTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    meta: { suppressGlobalError: true },
  };
}
